import { editChallengeGroupScoreWeighting } from '@actions/challengeGroups/editChallengeGroupScoreWeighting';
import {
    getChallengeGroupsFetchError,
    getChallengeGroupsIsFetching,
    getChallengeGroupsIsPatching,
    getChallengeGroupsPatchError,
    getChallengeGroupsPatchSuccess,
} from '@selectors/challengeGroups';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { clearFieldErrors } from 'lib/src/redux/actions/fieldErrors';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChallengeGroup } from 'src/types/shared/ChallengeGroup';

const useEditChallengeGroupScoreWeighting = (
    challengeGroup: ChallengeGroup,
    hideModal: () => void,
): Hook => {
    const dispatch = useDispatch();

    const isFetching = useSelector(getChallengeGroupsIsFetching);
    const fetchError = useSelector(getChallengeGroupsFetchError);
    const isPatching = useSelector(getChallengeGroupsIsPatching);
    const patchError = useSelector(getChallengeGroupsPatchError);
    const patchSuccess = useSelector(getChallengeGroupsPatchSuccess);
    const prevProps = usePrevious({
        isFetching,
        patchSuccess,
        challengeGroup,
    });

    const [formState, handleChange] = useForm({
        deploymentScale: challengeGroup.scoreWeighting?.deploymentScale ?? 0.2,
        fitWithChallenge: challengeGroup.scoreWeighting?.fitWithChallenge ?? 0.2,
        credibilityCompany: challengeGroup.scoreWeighting?.credibilityCompany ?? 0.2,
        credibilityTechnology: challengeGroup.scoreWeighting?.credibilityTechnology ?? 0.2,
        fullOrPartialSolution: challengeGroup.scoreWeighting?.fullOrPartialSolution ?? 0.2,
    });

    useEffect(() => {
        if (patchSuccess && !prevProps.patchSuccess) {
            hideModal();
        }
    }, [patchSuccess, prevProps, hideModal]);

    const handleSubmit = () => {
        dispatch(editChallengeGroupScoreWeighting(challengeGroup.id, formState));
    };

    const handleWeightingChange = (name: keyof FormState, value: number) => {
        dispatch(clearFieldErrors());
        const percentageValue = value * 100;
        const rounded = Math.round(percentageValue) / 100;
        handleChange(name, rounded);
    };

    return {
        challengeGroup,
        isFetching,
        fetchError,
        formState,
        handleChange: handleWeightingChange,
        handleSubmit,
        isPatching,
        patchError,
        patchSuccess,
        prevSuccess: prevProps.patchSuccess,
    };
};

interface FormState {
    deploymentScale: number;
    fitWithChallenge: number;
    credibilityCompany: number;
    credibilityTechnology: number;
    fullOrPartialSolution: number;
}

interface Hook {
    challengeGroup: ChallengeGroup;
    isFetching: boolean;
    fetchError: string | null;
    isPatching: boolean;
    patchError: string | null;
    patchSuccess: boolean;
    prevSuccess: boolean;
    formState: FormState;
    handleChange: (name: keyof FormState, value: number) => void;
    handleSubmit: () => void;
}

export default useEditChallengeGroupScoreWeighting;
